/**
 * Compact horizontal recording controls for Public Recording
 * Single-line layout optimized for iframe embedding
 * Uses Canvas-based scrolling bar waveform visualization
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { Mic, Pause, Square } from 'lucide-react';

import { WaveformCanvas } from './WaveformCanvas';

interface CompactRecordingControlsProps {
  isRecording: boolean;
  isPaused: boolean;
  audioLevel: number;
  onStartRecording: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStopRecording: () => void;
}

export const CompactRecordingControls = ({
  isRecording,
  isPaused,
  audioLevel,
  onStartRecording,
  onPauseRecording,
  onResumeRecording,
  onStopRecording,
}: CompactRecordingControlsProps) => {
  return (
    <div className="relative w-full h-full flex items-center justify-center px-3">
      {/* Main Content - Single Horizontal Line (fixed height prevents layout shift) */}
      <div className="relative z-10 flex items-center w-full gap-3 max-h-12 h-auto">
        <AnimatePresence mode="wait">
          {!isRecording ? (
            // Before Recording: Start Button (centered)
            <motion.div
              key="start-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center w-full">
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <motion.button
                    onClick={onStartRecording}
                    aria-label="Start recording"
                    className="relative flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-800 rounded-full flex items-center justify-center shadow-sm transition-all duration-300 hover:scale-105 active:scale-95 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                    whileTap={{ scale: [1, 0.92, 1.05, 1] }}
                    transition={{ duration: 0.3 }}>
                    <Mic className="w-[16px] h-[16px] text-white z-10" />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6} className="z-50">
                  <p>Start recording</p>
                </TooltipContent>
              </Tooltip>
            </motion.div>
          ) : (
            // During Recording: Waveform + Buttons
            <motion.div
              key="recording-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center w-full gap-3">
              {/* Waveform Visualization */}
              <WaveformCanvas
                audioLevel={audioLevel}
                isActive={isRecording}
                isPaused={isPaused}
                className="flex-1 h-10 min-w-0"
              />

              {/* Right: Control Buttons */}
              <div className="flex items-center gap-4 flex-shrink-0">
                {/* Pause / Resume Button */}
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <motion.button
                      onClick={isPaused ? onResumeRecording : onPauseRecording}
                      aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
                      className="relative w-8 h-8 rounded-full flex items-center justify-center shadow-sm bg-white hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200/80 dark:border-slate-600 transition-all duration-200 hover:scale-105 active:scale-95 touch-manipulation overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                      whileTap={{ scale: 0.9 }}>
                      <AnimatePresence mode="wait">
                        {isPaused ? (
                          <motion.div
                            key="mic"
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.5, opacity: 0 }}
                            transition={{ duration: 0.15 }}>
                            <Mic className="w-[14px] h-[14px] text-slate-700 dark:text-slate-200 relative z-10" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="pause"
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.5, opacity: 0 }}
                            transition={{ duration: 0.15 }}>
                            <Pause className="w-[14px] h-[14px] text-slate-700 dark:text-slate-200 relative z-10" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6} className="z-50">
                    <p>{isPaused ? 'Resume' : 'Pause'}</p>
                  </TooltipContent>
                </Tooltip>

                {/* Stop Button */}
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <motion.button
                      onClick={onStopRecording}
                      aria-label="Stop recording and upload"
                      className="relative w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-sm border border-red-400/20 transition-all duration-200 hover:scale-105 active:scale-95 touch-manipulation overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                      whileTap={{ scale: 0.9 }}>
                      <Square
                        className="w-[14px] h-[14px] text-white relative z-10"
                        fill="white"
                        strokeWidth={0}
                      />
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6} className="z-50">
                    <p>Stop & upload</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
